// Layer 2 (Engine): excursions × structured FMEA rules → scored cases.
// See docs/engines/diagnosis.md.

import type { Alarm, Case, CaseBranch, EvidenceRequest, VariationEvent } from '@/versions/v1/schema/case';
import type { DiagnosticRule, TriggerClause, TriggerCondition } from '@/versions/v1/schema/diagnostic-rule';
import type { ExcursionEvent, SpcRead } from '@/versions/v1/schema/spc';
import type { SignalRole, TagMapping } from '@/versions/v1/schema/tag-mapping';
import type { ScenarioPhase, SessionState } from '@/versions/v1/schema/scenario';
import type { Reading } from '@/versions/v1/schema/reading';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { INITIAL_ADVISORY_STATE, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import {
  CONTROL_LOOPS,
  DIAGNOSTIC_RULES,
  SUBPROCESSES,
  UTILIZATION_RANKS,
} from '@/versions/v1/shared/bunker-hill-seed';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { reliabilityWeightForRole } from '@/versions/v1/shared/reliability.transform';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { diffMinutes } from '@/versions/v1/shared/clock.derive';
import { formatValue, readingStatus, utilisationPct } from '@/versions/v1/shared/reading.derive';
import { readingsAt, seriesAt, valueAt } from '@/versions/v1/shared/series.derive';
import { SERIES_BY_ID } from '@/versions/v1/shared/bunker-hill-series';
import { CASES, VARIATION_EVENTS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';
import { caseIdForVariable } from '@/versions/v1/shared/threads';

/** Upstream anchor only — SMBS step feeds card lines, not the diagnosis queue (advisory-depth #326). */
const UPSTREAM_ANCHOR_VARIABLE_IDS = new Set(['mv-reag-smbs']);

const CLOSED_CASE_STATES = new Set<Case['state']>(['approved', 'rejected', 'closed']);

/** Open seeded cases already narrate a variable — live rules must not duplicate them (advisory-depth MF9). */
function openSeededVariableIds(): Set<string> {
  return new Set(
    CASES
      .filter((row) => !CLOSED_CASE_STATES.has(row.state))
      .flatMap((row) => {
        const event = VARIATION_EVENTS.find((ve) => ve.id === row.variationEventId);
        return event?.variableId ? [event.variableId] : [];
      }),
  );
}
import { longTrend, spcRead } from '@/versions/v1/shared/SpcEngine';

const WEIGHT_CLAUSE = 0.5;
const WEIGHT_RIVAL = 0.15;
const WEIGHT_DISC = 0.16;
const WEIGHT_GATE = 0.1;

const MAPPING_BY_ROLE = new Map(TAG_MAPPING.map((row) => [row.role, row]));
const RULE_BY_ID = new Map(DIAGNOSTIC_RULES.map((row) => [row.id, row]));

const LOOP_BY_ID = new Map(CONTROL_LOOPS.map((row) => [row.id, row]));
const READING_BY_ID = new Map(READINGS.map((row) => [row.id, row]));
const VARIABLE_BY_ID = new Map(MONITORED_VARIABLES.map((row) => [row.id, row]));
const SEVERITY_RANK = { strong: 2, signal: 1 } as const;

export type SignalRead = {
  role: SignalRole;
  mapping: TagMapping;
  spc: SpcRead | null;
  trend: SpcRead['trend'];
  longTrend: SpcRead['trend'];
  events: readonly ExcursionEvent[];
  latestValue?: number;
};

export type ScoredCandidate = {
  rule: DiagnosticRule;
  excursion: ExcursionEvent;
  clauseScore: number;
  rivalScore: number;
  discriminatorSatisfied: boolean;
  discriminatorOpen: boolean;
  discriminatorContradicts: boolean;
  gateHeld: boolean;
  gateFactor: number;
  confidencePct: number;
  satisfiedLabels: string[];
};

export type Diagnosis = {
  signals: readonly SignalRead[];
  candidates: readonly ScoredCandidate[];
  cases: readonly Case[];
  phase: ScenarioPhase;
  variationEvents: readonly VariationEvent[];
  ruleSetVersion: string;
};

function variableIdForRole(role: SignalRole): string | undefined {
  const map = MAPPING_BY_ROLE.get(role);
  if (!map?.seriesId) return undefined;
  return `mv-${map.seriesId.replace(/^rs-/, '')}`;
}

function spcForRole(role: SignalRole, state: SessionState): SpcRead | null {
  const variableId = variableIdForRole(role);
  if (!variableId) return null;
  try {
    return spcRead(variableId, state);
  } catch {
    return null;
  }
}

function buildSignals(state: SessionState): SignalRead[] {
  return TAG_MAPPING.map((mapping) => {
    const spc = mapping.seriesId ? spcForRole(mapping.role, state) : null;
    const latestValue = valueAt(mapping.readingId, state);
    const long = longTrend(mapping.role, state);
    return {
      role: mapping.role,
      mapping,
      spc,
      trend: spc?.trend ?? 'stable',
      longTrend: long?.trend ?? 'stable',
      events: spc?.events ?? [],
      latestValue,
    };
  });
}

function evaluateCondition(
  condition: TriggerCondition,
  signal: SignalRead,
): boolean {
  switch (condition) {
    case 'rising':
      return (
        signal.trend === 'rising' ||
        signal.events.some(
          (event) =>
            event.direction === 'up' &&
            (event.rule === 'six-point-trend' || event.rule === 'beyond-limit'),
        )
      );
    case 'falling':
      return (
        signal.trend === 'falling' ||
        signal.events.some(
          (event) =>
            event.direction === 'down' &&
            (event.rule === 'six-point-trend' || event.rule === 'beyond-limit'),
        )
      );
    case 'stable':
      return signal.trend === 'stable';
    case 'rising-long':
      return signal.longTrend === 'rising';
    case 'falling-long':
      return signal.longTrend === 'falling';
    case 'excursion':
      return signal.events.length > 0;
    case 'above-band':
    case 'below-band': {
      const reading = READING_BY_ID.get(signal.mapping.readingId);
      const value = signal.latestValue ?? reading?.value;
      if (value === undefined) return false;
      const hi = reading?.band?.hi ?? reading?.design;
      const lo = reading?.band?.lo ?? reading?.design;
      if (condition === 'above-band') return hi !== undefined && value > hi;
      return lo !== undefined && value < lo;
    }
    default:
      return false;
  }
}

function clauseHolds(clause: TriggerClause, signals: readonly SignalRead[]): boolean {
  const signal = signals.find((row) => row.role === clause.signal);
  if (!signal) return false;
  return evaluateCondition(clause.condition, signal);
}

function resolveSubprocessForExcursion(event: ExcursionEvent): string | undefined {
  const suffix = event.variableId.replace(/^mv-/, '');
  const map = TAG_MAPPING.find((row) => row.seriesId === `rs-${suffix}`);
  return map?.subprocessId;
}

function subprocessName(subprocessId: string | undefined): string {
  if (!subprocessId) return 'Plant';
  return SUBPROCESSES.find((row) => row.id === subprocessId)?.name ?? subprocessId;
}

function bottleneckSubprocessId(state: SessionState): string {
  const readings = readingsAt(state);
  let bestId = 'sp-grind';
  let bestPct = -1;
  for (const rank of UTILIZATION_RANKS) {
    if (rank.scope !== 'subprocess') continue;
    const reading = rank.readingId ? readings.find((row) => row.id === rank.readingId) : undefined;
    const pct =
      (reading ? utilisationPct(reading) : undefined) ??
      rank.utilizationPct ??
      0;
    if (pct > bestPct) {
      bestPct = pct;
      bestId = rank.id.startsWith('u-') ? `sp-${rank.id.slice(2)}` : rank.id;
    }
  }
  return bestId;
}

function isCaseWorthy(event: ExcursionEvent, state: SessionState): boolean {
  const variable = VARIABLE_BY_ID.get(event.variableId);
  if (!variable) return false;
  if (variable.tier === 'fixed') return true;
  if (event.severity === 'strong') {
    return resolveSubprocessForExcursion(event) === bottleneckSubprocessId(state);
  }
  return false;
}

function strongestEvent(events: readonly ExcursionEvent[]): ExcursionEvent {
  return [...events].sort((a, b) => {
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    return b.detectedAt.localeCompare(a.detectedAt);
  })[0]!;
}

function eventsByVariable(allExcursions: readonly ExcursionEvent[]): Map<string, ExcursionEvent[]> {
  const map = new Map<string, ExcursionEvent[]>();
  for (const event of allExcursions) {
    const list = map.get(event.variableId) ?? [];
    list.push(event);
    map.set(event.variableId, list);
  }
  return map;
}

function valueAtStamp(readingId: string, state: SessionState): { value: number; at: SeriesTime } | undefined {
  const reading = READING_BY_ID.get(readingId);
  const seriesId = TAG_MAPPING.find((row) => row.readingId === readingId)?.seriesId;
  if (seriesId) {
    const view = seriesAt(seriesId, state);
    if (view.latest) return { value: view.latest.value, at: view.latest.at };
  }
  if (reading?.value !== undefined) {
    return { value: reading.value, at: state.cursor };
  }
  return undefined;
}

function valueAtDetectedAt(
  readingId: string,
  detectedAt: SeriesTime,
  state: SessionState,
): number | undefined {
  const seriesId = TAG_MAPPING.find((row) => row.readingId === readingId)?.seriesId;
  if (!seriesId) return undefined;
  const view = seriesAt(seriesId, state);
  const point = view.points.find((p) => p.at === detectedAt && p.value !== null);
  if (point && point.value !== null) return point.value;
  const atOrBefore = [...view.points]
    .filter((p) => p.value !== null && diffMinutes(p.at, detectedAt) <= 0)
    .pop();
  return atOrBefore?.value ?? undefined;
}

function parseAnswerNumber(answer: string): number | undefined {
  const match = answer.match(/([\d.]+)/);
  if (!match) return undefined;
  const n = Number.parseFloat(match[1]!);
  return Number.isFinite(n) ? n : undefined;
}

function compareValue(value: number, op: string, threshold: number): boolean {
  switch (op) {
    case '>':
      return value > threshold;
    case '<':
      return value < threshold;
    case '>=':
      return value >= threshold;
    case '<=':
      return value <= threshold;
    default:
      return false;
  }
}

function evaluateDiscriminator(
  rule: DiagnosticRule,
  excursion: ExcursionEvent,
  state: SessionState,
  advisory?: AdvisorySessionState,
): { satisfied: boolean; open: boolean; contradicts: boolean; value?: number } {
  const disc = rule.discriminator;
  if (!disc) return { satisfied: true, open: false, contradicts: false };

  const caseId = caseIdForVariable(excursion.variableId);
  const evidenceId = `ev-${caseId}`;
  const answered = advisory?.answeredEvidence.get(evidenceId);
  const readingId = MAPPING_BY_ROLE.get(disc.signal)?.readingId ?? '';

  if (answered) {
    const value = parseAnswerNumber(answered.answer);
    if (value === undefined) return { satisfied: false, open: true, contradicts: false };
    const answerAt = answered.answeredAt ?? state.cursor;
    if (diffMinutes(answerAt, excursion.detectedAt) < 0) {
      return { satisfied: false, open: true, contradicts: false };
    }
    const satisfied = compareValue(value, disc.compare, disc.threshold);
    return {
      satisfied,
      open: !satisfied,
      contradicts: !satisfied,
      value,
    };
  }

  const latest = valueAtStamp(readingId, state);
  if (!latest) return { satisfied: false, open: true, contradicts: false };
  if (diffMinutes(latest.at, excursion.detectedAt) < 0) {
    return { satisfied: false, open: true, contradicts: false };
  }
  const satisfied = compareValue(latest.value, disc.compare, disc.threshold);
  return {
    satisfied,
    open: !satisfied,
    contradicts: !satisfied,
    value: latest.value,
  };
}

function scoreRule(
  rule: DiagnosticRule,
  signals: readonly SignalRead[],
  excursion: ExcursionEvent,
  state: SessionState,
  advisory?: AdvisorySessionState,
): ScoredCandidate | null {
  if (!rule.when || rule.when.length === 0 || !rule.subprocessId) return null;

  const primaryClause = rule.when[0];
  if (!primaryClause || !clauseHolds(primaryClause, signals)) return null;
  if (resolveSubprocessForExcursion(excursion) !== rule.subprocessId) return null;

  const roleVariableIds = new Set(
    rule.when
      .map((clause) => variableIdForRole(clause.signal))
      .filter((id): id is string => Boolean(id)),
  );
  if (!roleVariableIds.has(excursion.variableId)) return null;

  const satisfied = rule.when.filter((clause) => clauseHolds(clause, signals));
  if (satisfied.length === 0) return null;

  const totalWeight = rule.when.reduce((sum, clause) => sum + reliabilityWeightForRole(clause.signal), 0);
  const satisfiedWeight = satisfied.reduce((sum, clause) => sum + reliabilityWeightForRole(clause.signal), 0);
  const clauseScore = totalWeight > 0 ? satisfiedWeight / totalWeight : 0;
  const rivalRule = rule.rival ? RULE_BY_ID.get(rule.rival) : undefined;
  let rivalScore = 0;
  if (rivalRule?.when?.[0] && clauseHolds(rivalRule.when[0], signals)) {
    const rivalSatisfied = rivalRule.when.filter((clause) => clauseHolds(clause, signals));
    rivalScore = rivalSatisfied.length / rivalRule.when.length;
  }

  const disc = evaluateDiscriminator(rule, excursion, state, advisory);
  const primaryRole = rule.when[0]!.signal;
  const gateLoopId = MAPPING_BY_ROLE.get(primaryRole)?.gateLoopId;
  const loop = gateLoopId ? LOOP_BY_ID.get(gateLoopId) : undefined;
  const gateHeld = loop !== undefined && (loop.mode === 'manual' || loop.mode === 'local');
  const gateFactor = gateHeld ? 0.4 : 1;

  const discriminatorWeight = disc.satisfied ? 1 : disc.contradicts ? -0.5 : 0;
  const confidencePct = Math.max(
    0,
    Math.round(
      100 *
        (WEIGHT_CLAUSE * clauseScore +
          WEIGHT_RIVAL * (1 - rivalScore) +
          WEIGHT_DISC * discriminatorWeight +
          WEIGHT_GATE * gateFactor),
    ),
  );

  return {
    rule,
    excursion,
    clauseScore,
    rivalScore,
    discriminatorSatisfied: disc.satisfied,
    discriminatorOpen: disc.open && !disc.contradicts,
    discriminatorContradicts: disc.contradicts,
    gateHeld,
    gateFactor,
    confidencePct,
    satisfiedLabels: satisfied.map((c) => c.label),
  };
}

function cadenceUnits(variableId: string): { singular: string; plural: string } {
  const variable = VARIABLE_BY_ID.get(variableId);
  const cadence = variable ? SERIES_BY_ID[variable.seriesId]?.cadence : 'shift';
  if (cadence === 'hourly') return { singular: 'hour', plural: 'hours' };
  if (cadence === 'daily') return { singular: 'day', plural: 'days' };
  return { singular: 'shift', plural: 'shifts' };
}

function buildEscalationTitle(event: ExcursionEvent): string {
  const reading = READING_BY_ID.get(event.readingId);
  const label = reading?.label ?? event.variableId;
  const { plural } = cadenceUnits(event.variableId);

  let rulePhrase = '';
  if (event.rule === 'six-point-trend') {
    rulePhrase = `six ${plural} ${event.direction === 'up' ? 'rising' : 'falling'}`;
  } else if (event.rule === 'beyond-limit') {
    rulePhrase = `beyond ${event.direction === 'up' ? 'UCL' : 'LCL'}`;
  } else if (event.rule === 'widening-variability') {
    rulePhrase = 'widening variability';
  } else {
    rulePhrase = 'cyclical pattern';
  }

  let title = `${label} — ${rulePhrase}`;
  if (event.conforming !== undefined) {
    title += `, ${event.conforming ? 'conforming' : 'non-conforming'}`;
  }
  if (event.pointsToSpec !== undefined && event.boundUsed) {
    if (event.boundUsed === 'spec') {
      title += `; out of spec in ${event.pointsToSpec} ${plural} at this rate`;
    } else if (reading) {
      const designStr =
        reading.design !== undefined
          ? formatValue(reading, { value: reading.design, withUnit: true })
          : '';
      title += `; below design (${designStr}) in ${event.pointsToSpec} ${plural} at this rate`;
    }
  }
  return title;
}

function raisedAtClock(at: SeriesTime): string {
  return at.split('T')[1] ?? '00:00';
}

function observationForSignal(
  clause: TriggerClause,
  signals: readonly SignalRead[],
  excursion: ExcursionEvent,
  state: SessionState,
): string {
  const signal = signals.find((row) => row.role === clause.signal);
  const reading = READING_BY_ID.get(signal?.mapping.readingId ?? '');
  if (!reading || signal?.latestValue === undefined) return '';
  const atDetect = valueAtDetectedAt(reading.id, excursion.detectedAt, state);
  const since = raisedAtClock(excursion.detectedAt);
  if (signal.trend === 'stable' && signal.events.length === 0) {
    return `${reading.label} steady ${formatValue(reading, { value: signal.latestValue, withUnit: true })}`;
  }
  const fromVal =
    atDetect !== undefined
      ? formatValue(reading, { value: atDetect, withUnit: true })
      : '—';
  // The excursion's own direction beats the test-span trend: a six-point run
  // can fire "up" while the 24-point least-squares slope still reads stable.
  const dir = signal.events.find((e) => e.direction)?.direction;
  const verb = dir === 'up' ? 'rising' : dir === 'down' ? 'falling' : signal.trend;
  return `${reading.label} ${formatValue(reading, { value: signal.latestValue, withUnit: true })}, ${verb} from ${fromVal} since ${since}`;
}

function buildObservation(
  candidate: ScoredCandidate,
  signals: readonly SignalRead[],
  state: SessionState,
): string {
  const seen = new Set<SignalRole>();
  const parts: string[] = [];
  for (const clause of candidate.rule.when ?? []) {
    if (seen.has(clause.signal)) continue;
    seen.add(clause.signal);
    const phrase = observationForSignal(clause, signals, candidate.excursion, state);
    if (phrase) parts.push(phrase);
  }
  return `${parts.join(' · ')}.`;
}

function buildCauseCandidate(candidate: ScoredCandidate, signals: readonly SignalRead[]): string {
  let text = candidate.rule.diagnosis;
  if (candidate.rule.rival) {
    const rival = RULE_BY_ID.get(candidate.rule.rival);
    if (rival && candidate.rivalScore > 0) {
      const rivalClause = rival.when?.[0];
      const rivalSignal = signals.find((row) => row.role === rivalClause?.signal);
      text += ` Not ${rival.diagnosis} — ${rivalClause?.label ?? rival.signalPattern} reads ${rivalSignal?.trend ?? 'stable'}.`;
    }
  }
  return text;
}

function buildIfThen(candidate: ScoredCandidate): string {
  const labels = candidate.satisfiedLabels.join(' AND ');
  return `IF ${labels} THEN ${candidate.rule.diagnosis} — ${candidate.rule.recommendedAction}`;
}

function buildHeadline(candidate: ScoredCandidate, readings: readonly Reading[]): Case['headline'] | undefined {
  const primary = candidate.rule.when?.[0];
  if (!primary) return undefined;
  const map = MAPPING_BY_ROLE.get(primary.signal);
  const reading = readings.find((row) => row.id === map?.readingId);
  if (!reading) return undefined;
  const status = readingStatus(reading);
  const deltaDir =
    candidate.excursion.direction === 'up'
      ? 'up'
      : candidate.excursion.direction === 'down'
        ? 'down'
        : 'flat';
  return {
    label: reading.label,
    value: formatValue(reading, { withUnit: true }),
    basis:
      reading.design !== undefined
        ? { label: 'Design', value: formatValue(reading, { value: reading.design, withUnit: true }) }
        : undefined,
    delta: {
      text: candidate.excursion.rule.replace(/-/g, ' '),
      direction: deltaDir,
      tone: status.tone === 'bad' ? 'bad' : status.tone === 'warn' ? 'warn' : 'info',
    },
    provenance: 'computed from series · rules',
  };
}

function escalatedHeadline(event: ExcursionEvent, state: SessionState): Case['headline'] | undefined {
  const reading = READING_BY_ID.get(event.readingId);
  if (!reading) return undefined;
  const status = readingStatus(reading);
  const latest = valueAt(reading.id, state);
  const atStart = valueAtDetectedAt(reading.id, event.detectedAt, state);
  const deltaText =
    latest !== undefined && atStart !== undefined
      ? formatValue(reading, { value: latest - atStart, withUnit: true })
      : event.rule.replace(/-/g, ' ');
  return {
    label: reading.label,
    value: formatValue(reading, { withUnit: true, value: latest }),
    basis:
      event.boundUsed === 'spec' && reading.spec?.lo !== undefined
        ? { label: 'Spec lo', value: formatValue(reading, { value: reading.spec.lo, withUnit: true }) }
        : reading.design !== undefined
          ? { label: 'Design', value: formatValue(reading, { value: reading.design, withUnit: true }) }
          : undefined,
    delta: {
      text: deltaText,
      direction: event.direction === 'up' ? 'up' : event.direction === 'down' ? 'down' : 'flat',
      tone: status.tone === 'bad' ? 'bad' : status.tone === 'warn' ? 'warn' : 'info',
    },
    provenance: 'excursion',
  };
}

function escalatedCode(event: ExcursionEvent): string {
  const suffix = event.variableId.replace(/^mv-/, '');
  const map = TAG_MAPPING.find((row) => row.seriesId === `rs-${suffix}`);
  return map?.instrumentTag ? `EX-${map.instrumentTag}` : `EX-${suffix.toUpperCase()}`;
}

function prioritySeverity(priority: DiagnosticRule['priority']): Case['severity'] {
  if (priority === 'P1') return 'high';
  if (priority === 'P2') return 'med';
  if (priority === 'P3') return 'low';
  return 'low';
}

function excursionSeverity(severity: ExcursionEvent['severity']): Case['severity'] {
  return severity === 'strong' ? 'med' : 'low';
}

function evidenceTarget(kind: EvidenceRequest['kind']): Case['assignedTo'] {
  if (kind === 'lab-sample') return 'operator';
  return 'cro';
}

function buildCaseFromCandidate(
  candidate: ScoredCandidate,
  signals: readonly SignalRead[],
  excursionsByVariable: ReadonlyMap<string, readonly ExcursionEvent[]>,
  readings: readonly Reading[],
  state: SessionState,
  advisory?: AdvisorySessionState,
): Case {
  const { rule, excursion, confidencePct, gateHeld, discriminatorOpen, discriminatorSatisfied, discriminatorContradicts } =
    candidate;
  const variableEvents = excursionsByVariable.get(excursion.variableId) ?? [excursion];
  const primaryExcursion = strongestEvent(variableEvents);
  const caseId = caseIdForVariable(excursion.variableId);
  const evidenceId = `ev-${caseId}`;

  let branch: CaseBranch;
  let caseState: Case['state'];
  if (discriminatorContradicts) {
    branch = 'escalate';
    caseState = 'escalated';
  } else if (confidencePct >= 60 && (discriminatorSatisfied || !rule.discriminator)) {
    branch = 'zero-touch';
    caseState = 'routed';
  } else if (confidencePct >= 40 && discriminatorOpen && rule.discriminator) {
    branch = 'ask';
    caseState = 'evidence-requested';
  } else if (confidencePct >= 40) {
    branch = 'zero-touch';
    caseState = 'routed';
  } else {
    branch = 'escalate';
    caseState = 'escalated';
  }

  const evidenceRequests: EvidenceRequest[] = [];
  if (gateHeld) {
    const loop = LOOP_BY_ID.get(MAPPING_BY_ROLE.get(rule.when![0]!.signal)?.gateLoopId ?? '');
    evidenceRequests.push({
      id: `ev-${caseId}-loop`,
      kind: 'loop-state',
      target: 'cro',
      text: loop ? `confirm ${loop.tag} back in auto` : 'confirm loop back in auto',
      status: 'open',
    });
  }
  if (branch === 'ask' && rule.discriminator) {
    evidenceRequests.push({
      id: evidenceId,
      kind: rule.discriminator.evidenceKind,
      target: evidenceTarget(rule.discriminator.evidenceKind),
      text: rule.discriminator.label,
      status: advisory?.answeredEvidence.has(evidenceId) ? 'answered' : 'open',
      answer: advisory?.answeredEvidence.get(evidenceId)?.answer,
      answeredAt: advisory?.answeredEvidence.get(evidenceId)?.answeredAt,
    });
  }

  const kind = rule.playableScenario === 'hardness' ? 'hardness' : undefined;

  return {
    id: caseId,
    code: rule.id.toUpperCase().replace('DR-', ''),
    variationEventId: primaryExcursion.id,
    title:
      branch === 'escalate' && discriminatorContradicts
        ? `${rule.diagnosis} — evidence contradicts pattern`
        : branch === 'escalate'
          ? buildEscalationTitle(primaryExcursion)
          : rule.diagnosis,
    circuit: rule.subprocessId === 'sp-grind' ? 'Primary grinding' : subprocessName(rule.subprocessId),
    state: caseState,
    branch,
    kind,
    assignedTo: rule.responsible,
    severity:
      branch === 'escalate'
        ? excursionSeverity(excursion.severity)
        : caseState === 'routed'
          ? 'high'
          : prioritySeverity(rule.priority),
    aiConfidencePct: confidencePct,
    observation: buildObservation(candidate, signals, state),
    causeCandidate: buildCauseCandidate(candidate, signals),
    ifThen: buildIfThen(candidate),
    controlModeCheck: gateHeld
      ? {
          tag: LOOP_BY_ID.get(MAPPING_BY_ROLE.get(rule.when![0]!.signal)?.gateLoopId ?? '')?.tag ?? '',
          mode: LOOP_BY_ID.get(MAPPING_BY_ROLE.get(rule.when![0]!.signal)?.gateLoopId ?? '')?.mode ?? 'manual',
        }
      : undefined,
    evidenceRequests,
    headline: buildHeadline(candidate, readings),
    raisedAt: raisedAtClock(primaryExcursion.detectedAt),
  };
}

function buildEscalatedCase(event: ExcursionEvent, state: SessionState): Case {
  const subprocessId = resolveSubprocessForExcursion(event);
  const reading = READING_BY_ID.get(event.readingId);
  const latest = reading ? valueAt(reading.id, state) : undefined;
  const obsParts = [`${reading?.label ?? event.variableId} excursion open`];
  if (latest !== undefined && reading) {
    obsParts.unshift(
      `${reading.label} ${formatValue(reading, { value: latest, withUnit: true })}, ${event.rule.replace(/-/g, ' ')} since ${raisedAtClock(event.detectedAt)}`,
    );
  }
  return {
    id: caseIdForVariable(event.variableId),
    code: escalatedCode(event),
    variationEventId: event.id,
    title: buildEscalationTitle(event),
    circuit: subprocessName(subprocessId),
    state: 'escalated',
    branch: 'escalate',
    kind: 'new-pattern',
    assignedTo: 'metallurgist',
    severity: excursionSeverity(event.severity),
    observation: `${obsParts.join(' · ')}.`,
    causeCandidate: 'No structured rule matched — metallurgist review',
    ifThen: 'IF excursion open THEN review signals and escalate if needed.',
    evidenceRequests: [],
    headline: escalatedHeadline(event, state),
    raisedAt: raisedAtClock(event.detectedAt),
  };
}

function buildVariationEvent(candidate: ScoredCandidate): VariationEvent {
  return {
    id: candidate.excursion.id,
    subprocessId: candidate.rule.subprocessId!,
    detectedAt: raisedAtClock(candidate.excursion.detectedAt),
    description: candidate.rule.signalPattern,
    magnitude: candidate.excursion.rule.replace(/-/g, ' '),
    matchedRules: [
      { ruleId: candidate.rule.id, confidencePct: candidate.confidencePct },
      ...(candidate.rivalScore > 0 && candidate.rule.rival
        ? [{ ruleId: candidate.rule.rival, confidencePct: Math.round(candidate.rivalScore * 100) }]
        : []),
    ],
  };
}

function derivePhase(candidates: readonly ScoredCandidate[]): ScenarioPhase {
  const hardness = candidates.find((c) => c.rule.id === 'dr-hardness');
  const top = candidates[0];
  if (!hardness || top?.rule.id !== 'dr-hardness') return 'idle';
  if (hardness.discriminatorSatisfied) return 'confirmed';
  if (hardness.confidencePct >= 40 && hardness.discriminatorOpen) return 'provisional';
  return 'idle';
}

export function diagnose(
  state: SessionState,
  advisory: AdvisorySessionState = INITIAL_ADVISORY_STATE,
): Diagnosis {
  const ruleSet = liveRuleSet(advisory);
  const liveRules = ruleSet.rules;
  const signals = buildSignals(state);
  const allExcursions = signals
    .flatMap((signal) => signal.events)
    .sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
      b.detectedAt.localeCompare(a.detectedAt));
  const readings = readingsAt(state);
  const byVariable = eventsByVariable(allExcursions);
  const primaryByVariable = new Map<string, ExcursionEvent>();
  for (const [variableId, list] of byVariable) {
    primaryByVariable.set(variableId, strongestEvent(list));
  }

  const seededVariables = openSeededVariableIds();
  const candidates: ScoredCandidate[] = [];
  for (const rule of liveRules) {
    if (!rule.when || !rule.subprocessId) continue;
    const subprocessEvents = allExcursions.filter(
      (e) => resolveSubprocessForExcursion(e) === rule.subprocessId,
    );
    let best: ScoredCandidate | null = null;
    for (const excursion of subprocessEvents) {
      const scored = scoreRule(rule, signals, excursion, state, advisory);
      if (!scored) continue;
      if (!best) {
        best = scored;
      } else if (scored.clauseScore > best.clauseScore) {
        best = scored;
      } else if (
        scored.clauseScore === best.clauseScore &&
        SEVERITY_RANK[scored.excursion.severity] > SEVERITY_RANK[best.excursion.severity]
      ) {
        best = scored;
      }
    }
    if (best && !seededVariables.has(best.excursion.variableId)) {
      candidates.push(best);
    }
  }

  candidates.sort((a, b) => b.confidencePct - a.confidencePct);

  const matchedVariableIds = new Set<string>();
  const cases: Case[] = [];
  const variationEvents: VariationEvent[] = [];

  for (const candidate of candidates) {
    if (candidate.confidencePct < 40) continue;
    if (UPSTREAM_ANCHOR_VARIABLE_IDS.has(candidate.excursion.variableId)) continue;
    if (matchedVariableIds.has(candidate.excursion.variableId)) continue;
    matchedVariableIds.add(candidate.excursion.variableId);
    cases.push(buildCaseFromCandidate(candidate, signals, byVariable, readings, state, advisory));
    variationEvents.push(buildVariationEvent(candidate));
  }

  for (const [variableId, event] of primaryByVariable) {
    if (matchedVariableIds.has(variableId)) continue;
    if (UPSTREAM_ANCHOR_VARIABLE_IDS.has(variableId)) continue;
    if (!isCaseWorthy(event, state)) continue;
    cases.push(buildEscalatedCase(event, state));
  }

  for (const rule of liveRules) {
    if (rule.when?.length) continue;
    for (const event of VARIATION_EVENTS) {
      if (!event.matchedRules.some((row) => row.ruleId === rule.id)) continue;
      if (variationEvents.some((row) => row.id === event.id)) continue;
      variationEvents.push(event);
    }
  }

  cases.sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));

  return {
    signals,
    candidates,
    cases,
    phase: derivePhase(candidates),
    variationEvents,
    ruleSetVersion: ruleSet.version.id,
  };
}

export function phaseForDiagnosis(diagnosis: Diagnosis): ScenarioPhase {
  return diagnosis.phase;
}

export function variationEventsForDiagnosis(diagnosis: Diagnosis): readonly VariationEvent[] {
  return diagnosis.variationEvents;
}

// ---------------------------------------------------------------------------
// § Alarm boundary (#[priority-likelihood].S3) — a signal already covered by
// a seeded DCS alarm does not additionally route to the CRO's own excursion
// list; it stays visible on the Met feed, a different audience with a
// different job (process diagnosis, not alarm response). Coverage is
// *derived* from what each covering alarm actually names — its own
// instrument/loop tag, resolved through `TAG_MAPPING` — never a
// hand-maintained list of excluded case or alarm ids.
//
// Review priority-likelihood M7: only a `'dcs'` alarm (`Alarm.origin`) can
// cover a variable. That distinction is modelled on the alarm itself, seeded
// from what the corpus says each row is — never inferred from whether the
// row happens to carry a `tag`, which is a coincidence of the seed, not a
// fact about the alarm's source. An earlier version of this boundary also
// resolved coverage through the case an alarm spawned (`Alarm.spawnedCaseId`
// → `VariationEvent` → variable); that path is deleted. A case this product
// itself created is not independent coverage of anything the DCS raised —
// `alm-ag-conc`'s own message says "(advisory, see case)" — so an alarm with
// no `tag` at all (`alm-pump-04` — the seal-water alarm with no mapped
// monitored variable; `alm-ag-conc` — the product's own
// advisory) simply covers nothing, and is absent from the boundary rather
// than defaulted into it.
// ---------------------------------------------------------------------------

/** The alarm's own tag (`Alarm.tag`, e.g. `AIC5002`), resolved through the
 * plant's general-purpose instrument/loop tag registry — the same
 * `TAG_MAPPING` every SPC/governance lookup in this codebase already uses,
 * not a table built for this feature. */
function variableIdFromAlarmTag(tag: string | undefined): string | undefined {
  if (!tag) return undefined;
  const mapping = TAG_MAPPING.find((row) => row.instrumentTag === tag || row.loopTag === tag);
  if (!mapping?.seriesId) return undefined;
  return MONITORED_VARIABLES.find((row) => row.seriesId === mapping.seriesId)?.id;
}

/** The monitored variable a seeded DCS alarm concerns, or `undefined` when
 * the alarm is not a DCS alarm (`origin !== 'dcs'`) or its tag resolves to
 * nothing. This is the whole algorithm, no second path — see the boundary
 * note above for why the former case-spawn fallback was removed. */
export function variableIdForAlarm(alarm: Alarm): string | undefined {
  if (alarm.origin !== 'dcs') return undefined;
  return variableIdFromAlarmTag(alarm.tag);
}

/** Every monitored variable a seeded DCS alarm already covers — the CRO
 * excursion-list boundary. A variable in this set is already an alarm's job;
 * the CRO's own "Priority excursions" panel excludes it (still visible on
 * the Met feed, per `driftPanel`'s own scope, which this boundary does not
 * touch). Built by deriving each alarm's variable via `variableIdForAlarm`,
 * never by naming a case id directly. */
export function alarmCoveredVariableIds(alarms: readonly Alarm[]): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const alarm of alarms) {
    const variableId = variableIdForAlarm(alarm);
    if (variableId) ids.add(variableId);
  }
  return ids;
}

export function forecastPhraseForEvent(event: ExcursionEvent): string | undefined {
  if (event.pointsToSpec === undefined || !event.boundUsed) return undefined;
  const { plural } = cadenceUnits(event.variableId);
  if (event.boundUsed === 'spec') {
    return `out of spec in ${event.pointsToSpec} ${plural} at this rate`;
  }
  const reading = READING_BY_ID.get(event.readingId);
  const designStr =
    reading?.design !== undefined
      ? formatValue(reading, { value: reading.design, withUnit: true })
      : '';
  return `below design (${designStr}) in ${event.pointsToSpec} ${plural} at this rate`;
}
